import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token && password.length >= 8 && password === confirm && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      if (data?.user) setUser(data.user);
      toast.success("Password updated — you're signed in");
      nav("/app", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen pc-grid-bg flex items-center justify-center p-6">
      <div className="pc-card p-8 w-full max-w-md" data-testid="reset-password-card">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <span className="text-white font-bold" style={{fontFamily:'Outfit'}}>P</span>
          </div>
          <div className="font-bold text-white text-lg" style={{fontFamily:'Outfit'}}>Compass PM</div>
        </Link>
        <h1 className="text-2xl font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Choose a new password</h1>
        <p className="text-sm text-slate-400 mb-6">At least 8 characters. You'll be signed in right after.</p>

        {!token ? (
          <div className="text-sm text-rose-400" data-testid="reset-password-missing-token">
            This reset link is missing a token. Request a new link from the <Link to="/forgot-password" className="underline">Forgot password</Link> page.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">New password</label>
              <input
                data-testid="reset-password-new"
                type="password" required value={password}
                onChange={(e)=>setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500" />
              {tooShort && <div className="text-xs text-amber-400 mt-1">Must be at least 8 characters.</div>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Confirm new password</label>
              <input
                data-testid="reset-password-confirm"
                type="password" required value={confirm}
                onChange={(e)=>setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:border-blue-500" />
              {mismatch && <div className="text-xs text-rose-400 mt-1">Passwords don't match.</div>}
            </div>
            {err && <div data-testid="reset-password-error" className="text-sm text-rose-400">{err}</div>}
            <button
              data-testid="reset-password-submit" disabled={!canSubmit}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600 disabled:opacity-60">
              {busy ? "Updating…" : "Update password & sign in"}
            </button>
          </form>
        )}

        <div className="mt-6 text-sm text-slate-400 text-center">
          <Link to="/login" className="text-blue-400 hover:text-blue-300" data-testid="back-to-login-link">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
